// Looping product animations for project heroes (GSAP). This is the shared
// engine: it builds a fixed-size stage inside a container, scales the stage to
// the container's width, draws the drifting square grid, lazy-inits when the
// container nears the viewport, pauses when off-screen or on a hidden tab, and
// holds a single static frame when the visitor prefers reduced motion.
//
// What actually moves lives in a preset (frontend/javascript/hero_motion/*.js):
// the stage size, the layer art, and the timeline. One instance per
// `[data-hero-motion]`; the attribute value is the base URL for the layer
// images (e.g. "/images/projects/simpledocs/motion") and
// `data-hero-motion-preset` picks the preset (defaults to "simpledocs").
//
// A preset exports `{ stage: { w, h }, grid, focus, build(ctx) }`. `grid: false`
// skips the drifting square grid. `focus: { x, y, w, h, media }` names a
// window of the stage that should fill the container instead of the whole
// stage while `media` matches (used to zoom in on phones). `build` creates layers
// through `ctx.addLayer`, and returns `{ tl, idle }` (a paused-able GSAP
// timeline plus any looping idle tweens), or `null` under reduced motion after
// placing the static frame. The container's aspect ratio is set in CSS per
// preset and must match `stage`.
import gsap from "gsap"
import simpledocs from "./hero_motion/simpledocs.js"
import saverlife from "./hero_motion/saverlife.js"

const PRESETS = { simpledocs, saverlife }

const SQ = 50
const rnd = (a, b) => a + Math.random() * (b - a)

function initHeroMotion(container) {
  const preset = PRESETS[container.dataset.heroMotionPreset || "simpledocs"]
  if (!preset) return
  const base = (container.dataset.heroMotion || "").replace(/\/$/, "")
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const { w: STAGE_W, h: STAGE_H } = preset.stage
  const showGrid = preset.grid !== false

  const stage = document.createElement("div")
  stage.className = "hm-stage"
  stage.style.width = STAGE_W + "px"
  stage.style.height = STAGE_H + "px"
  const grid = document.createElement("canvas")
  grid.className = "hm-grid"
  if (showGrid) stage.appendChild(grid)
  container.appendChild(stage)

  // A positioned layer in stage coordinates. The inner `.hm-float` carries the
  // idle drift so the outer layer stays free for enter/exit tweens. Presets
  // may append extra elements to `layer._float` (masks, overlays).
  function addLayer({ src, x, y, w, h, central = false, className = "" }) {
    const layer = document.createElement("div")
    layer.className = "hm-layer" + (central ? " central" : "") + (className ? " " + className : "")
    layer.style.left = x + "px"
    layer.style.top = y + "px"
    layer.style.width = w + "px"
    layer.style.height = h + "px"

    const float = document.createElement("div")
    float.className = "hm-float"
    const img = document.createElement("img")
    img.src = `${base}/${src}`
    img.alt = ""
    img.loading = "lazy"
    img.decoding = "async"
    float.appendChild(img)
    layer.appendChild(float)
    stage.appendChild(layer)

    layer._float = float
    layer._img = img
    layer._central = central
    return layer
  }

  // Scale the stage to the container's width. With a matching `focus`, scale
  // and shift so that window of the stage fills the container instead; the
  // container's aspect ratio for that case is set in CSS per preset.
  const focusMedia = preset.focus ? window.matchMedia(preset.focus.media) : null
  function fit() {
    const box = focusMedia && focusMedia.matches ? preset.focus : { x: 0, y: 0, w: STAGE_W }
    const scale = container.clientWidth / box.w
    stage.style.transform = `translate(${-box.x * scale}px, ${-box.y * scale}px) scale(${scale})`
  }
  fit()
  const ro = new ResizeObserver(fit)
  ro.observe(container)
  if (focusMedia) focusMedia.addEventListener("change", fit)

  const gctx = grid.getContext("2d")
  function sizeGrid() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    grid.width = STAGE_W * dpr; grid.height = STAGE_H * dpr
    grid.style.width = STAGE_W + "px"; grid.style.height = STAGE_H + "px"
    gctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  let goff = 0
  function renderGrid() {
    if (!showGrid) return
    gctx.clearRect(0, 0, STAGE_W, STAGE_H)
    const o = SQ - goff
    gctx.lineWidth = 1
    gctx.strokeStyle = "rgba(70, 88, 112, 0.07)"
    for (let x = -SQ + o; x <= STAGE_W; x += SQ)
      for (let y = -SQ + o; y <= STAGE_H; y += SQ)
        gctx.strokeRect(x + 0.5, y + 0.5, SQ, SQ)
  }
  sizeGrid()

  const result = preset.build({ base, reduce, rnd, gsap, addLayer, STAGE_W, STAGE_H })
  renderGrid()
  if (!result) return

  const { tl, idle = [] } = result
  tl.pause()
  idle.forEach((t) => t.pause())
  // Debug hook: lets the console seek the loop (`el._heroMotion.tl.seek("scene3")`).
  container._heroMotion = { tl, idle }

  let running = false
  let rafId = 0
  let inView = false
  function gridLoop() {
    if (!showGrid) return
    goff = (goff + 0.28) % SQ
    renderGrid()
    rafId = requestAnimationFrame(gridLoop)
  }
  function sync() {
    const shouldRun = inView && !document.hidden
    if (shouldRun && !running) {
      running = true
      tl.resume()
      idle.forEach((t) => t.resume())
      gridLoop()
    } else if (!shouldRun && running) {
      running = false
      tl.pause()
      idle.forEach((t) => t.pause())
      cancelAnimationFrame(rafId)
    }
  }

  const io = new IntersectionObserver((entries) => {
    inView = entries[0].isIntersecting
    sync()
  }, { threshold: 0.15 })
  io.observe(container)
  document.addEventListener("visibilitychange", sync)
}

export default function setupHeroMotion() {
  const containers = document.querySelectorAll("[data-hero-motion]")
  // Give nearby demos time to load their first scene before scrolling into view.
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      observer.unobserve(entry.target)
      initHeroMotion(entry.target)
    })
  }, { rootMargin: "240px 0px" })
  containers.forEach((container) => observer.observe(container))
}
